import { SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import { AxiosError } from 'axios';

import { authApi } from '@Services/auth.service';
import axiosInstance from '@Services/axiosInstance.service';
import TokenService from '@Services/token.service';
import { logout, setCredentials } from '@Slices/auth.slice';
import { LoginResponse } from '@Typings/auth.types';

import { AppDispatch } from '../../store/store';

const setupAxiosListeners = (dispatch: AppDispatch): void => {
  /** Intercepts Request and adds AccessToken
   * Adds access token to all the API requests if the user is logged in and has a valid access token
   */
  axiosInstance.interceptors.request.use(
    (req) => {
      if (req.headers) {
        req.headers.Authorization = `Bearer ${
          TokenService.getAccessToken() || ''
        }`;
      }

      return req;
    },
    (err) => Promise.reject(err)
  );

  /** Intercepts Response
   *  In case the API errors with 401 i.e. unauthorized due to expired/invalid access token but valid refresh token,
   *    - gets a new pair of access & refresh token
   *    - updates the authStake with new tokens and also updates the localStorage
   *    - retry hitting the failed request
   */
  axiosInstance.interceptors.response.use(
    (res) => res,
    async (err: AxiosError) => {
      const originalRequest = err.config;

      if (err.response?.status === 401) {
        const refreshToken = TokenService.getRefreshToken();

        // if refresh token is not present or refresh token is invalid/expired, logout the user
        if (!refreshToken || TokenService.isTokenExpired(refreshToken)) {
          dispatch<{
            payload: undefined;
            type: string;
          }>(logout());
        }

        const refreshResult:
          | {
              data: LoginResponse;
            }
          | {
              error: FetchBaseQueryError | SerializedError;
            } = await dispatch(
          authApi.endpoints.getNewAccessToken.initiate(
            TokenService.getRefreshToken()
          )
        );

        if (refreshResult) {
          dispatch(
            setCredentials({
              accessToken: (refreshResult as { data: LoginResponse }).data
                .accessToken,
              refreshToken: (refreshResult as { data: LoginResponse }).data
                .refreshToken,
              isLoggedIn: true,
              role: TokenService.getUserRole(),
            })
          );
        } else {
          dispatch<{
            payload: undefined;
            type: string;
          }>(logout());
        }

        return axiosInstance(originalRequest);
      }
    }
  );
};

export default setupAxiosListeners;
